import fs from "fs-extra"
import path from "path"
import os from "os"
import { execa } from "execa"
import { logger } from "@/src/utils/logger"
import yaml from "yaml"

const TIPTAP_REGISTRY = "https://registry.tiptap.dev/"
const AUTH_TOKEN_KEY = "//registry.tiptap.dev/:_authToken"
const TIPTAP_PRO_REGISTRY_KEY = "@tiptap-pro:registry"

export type PackageManager = "npm" | "yarn" | "pnpm" | "bun"

/**
 * Add .npmrc to .gitignore if it's not already there
 */
async function addNpmrcToGitignore(cwd: string): Promise<void> {
  try {
    const gitignorePath = path.join(cwd, ".gitignore")
    const npmrcEntry = ".npmrc"
    let gitignoreContent = ""

    if (await fs.pathExists(gitignorePath)) {
      gitignoreContent = await fs.readFile(gitignorePath, "utf8")
    }

    const lines = gitignoreContent.split("\n")
    const hasNpmrcEntry = lines.some(
      (line) =>
        line.trim() === npmrcEntry ||
        line.trim() === "/.npmrc" ||
        line.trim() === "**/.npmrc"
    )

    if (!hasNpmrcEntry) {
      const newContent = gitignoreContent.trim()
        ? `${gitignoreContent}\n\n# Authentication tokens\n${npmrcEntry}\n`
        : `# Authentication tokens\n${npmrcEntry}\n`

      await fs.writeFile(gitignorePath, newContent)
    }
  } catch (error) {
    logger.warn(
      `Could not update .gitignore: ${error instanceof Error ? error.message : "Unknown error"}`
    )
  }
}

/**
 * Save authentication token to package manager config
 */
export async function saveAuthToken(
  token: string,
  packageManager: PackageManager,
  cwd: string
): Promise<boolean> {
  try {
    let createdNpmrc = false

    switch (packageManager) {
      case "npm":
        await saveNpmToken(token, cwd)
        createdNpmrc = true
        break
      case "yarn":
        createdNpmrc = await saveYarnToken(token, cwd)
        break
      case "pnpm":
      case "bun":
        await saveToNpmrc(path.join(cwd, ".npmrc"), token)
        createdNpmrc = true
        break
    }

    if (createdNpmrc) {
      await addNpmrcToGitignore(cwd)
    }

    return true
  } catch (error) {
    logger.error(
      `Error saving auth token: ${error instanceof Error ? error.message : "Unknown error"}`
    )
    return false
  }
}

/**
 * Save token for npm
 */
async function saveNpmToken(token: string, cwd: string): Promise<void> {
  await execa(
    "npm",
    [
      "config",
      "set",
      TIPTAP_PRO_REGISTRY_KEY,
      TIPTAP_REGISTRY,
      "--location=project",
    ],
    { cwd }
  )

  await execa(
    "npm",
    ["config", "set", AUTH_TOKEN_KEY, token, "--location=project"],
    { cwd }
  )
}

/**
 * Save token for yarn (handles v1 and v2+).
 *
 * Returns true when a project `.npmrc` was written (so the caller can decide
 * whether to gitignore it). Yarn Berry writes `.yarnrc.yml` instead, so it
 * returns false in that path.
 */
async function saveYarnToken(token: string, cwd: string): Promise<boolean> {
  try {
    const { stdout: yarnVersion } = await execa("yarn", ["--version"], { cwd })
    const isYarnV1 = yarnVersion.startsWith("1.")

    if (isYarnV1) {
      // Yarn classic silently ignores `--location=project` and writes to the
      // global `~/.yarnrc`, where it doesn't even consult that file for
      // npm-style scoped registry auth. Write the project `.npmrc` directly —
      // that's the file yarn 1 actually reads for `_authToken`.
      await saveToNpmrc(path.join(cwd, ".npmrc"), token)
      return true
    }
    await saveYarnBerryToken(token, cwd)
    return false
  } catch (error) {
    // Fallback to .npmrc for yarn
    await saveToNpmrc(path.join(cwd, ".npmrc"), token)
    return true
  }
}

/**
 * Save token for Yarn Berry (v2+)
 */
async function saveYarnBerryToken(token: string, cwd: string): Promise<void> {
  const yarnrcPath = path.join(cwd, ".yarnrc.yml")
  let yamlObj: Record<string, unknown> = {}

  // Read existing config if it exists
  if (fs.existsSync(yarnrcPath)) {
    const yarnrcContent = await fs.readFile(yarnrcPath, "utf8")
    try {
      yamlObj = yaml.parse(yarnrcContent) || {}
    } catch (e) {
      // If parsing fails, start with empty object
    }
  }

  // Ensure the structure exists
  if (!yamlObj.npmScopes) {
    yamlObj.npmScopes = {}
  }

  // Set or update the tiptap-pro scope. `npmAlwaysAuth` ensures Berry sends
  // the token on GET requests too, not just PUTs — the Tiptap Pro registry
  // requires auth on package metadata fetches.
  const npmScopes = yamlObj.npmScopes as Record<string, unknown>
  npmScopes["tiptap-pro"] = {
    npmRegistryServer: TIPTAP_REGISTRY,
    npmAuthToken: token,
    npmAlwaysAuth: true,
  }

  // Write back to file
  await fs.writeFile(yarnrcPath, yaml.stringify(yamlObj))
}

/**
 * Update .npmrc file with token
 */
async function saveToNpmrc(npmrcPath: string, token: string): Promise<void> {
  let npmrcContent = ""

  // Read existing file if it exists
  if (fs.existsSync(npmrcPath)) {
    npmrcContent = await fs.readFile(npmrcPath, "utf8")
  }

  // Parse .npmrc content
  const { lines, processedKeys } = parseNpmrc(npmrcContent, token)

  // Add missing keys
  if (!processedKeys.has(TIPTAP_PRO_REGISTRY_KEY)) {
    lines.push(`${TIPTAP_PRO_REGISTRY_KEY}=${TIPTAP_REGISTRY}`)
  }

  if (!processedKeys.has(AUTH_TOKEN_KEY)) {
    lines.push(`${AUTH_TOKEN_KEY}=${token}`)
  }

  // Ensure file ends with a newline
  if (lines.length > 0 && lines[lines.length - 1] !== "") {
    lines.push("")
  }

  // Write the updated content back to the file
  await fs.writeFile(npmrcPath, lines.join("\n"))
}

/**
 * Parse .npmrc content
 */
function parseNpmrc(
  npmrcContent: string,
  token: string
): {
  lines: string[]
  processedKeys: Set<string>
} {
  const lines: string[] = []
  const processedKeys = new Set<string>()

  // Split content into lines
  const contentLines = npmrcContent.split("\n")

  // Process each line
  for (const line of contentLines) {
    const trimmedLine = line.trim()

    // Skip empty lines or add them as-is
    if (!trimmedLine) {
      lines.push(line)
      continue
    }

    // Preserve comments
    if (trimmedLine.startsWith("#")) {
      lines.push(line)
      continue
    }

    // Process key-value pairs
    const index = line.indexOf("=")
    if (index !== -1) {
      const key = line.substring(0, index).trim()

      // Update specific keys
      if (key === TIPTAP_PRO_REGISTRY_KEY) {
        lines.push(`${TIPTAP_PRO_REGISTRY_KEY}=${TIPTAP_REGISTRY}`)
        processedKeys.add(key)
      } else if (key === AUTH_TOKEN_KEY) {
        lines.push(`${AUTH_TOKEN_KEY}=${token}`)
        processedKeys.add(key)
      } else {
        // Keep other keys unchanged
        lines.push(line)
      }
    } else {
      // Keep lines that aren't key-value pairs
      lines.push(line)
    }
  }

  return { lines, processedKeys }
}

/**
 * Get auth token from package manager config
 */
export async function getAuthToken(
  packageManager: PackageManager,
  cwd: string
): Promise<string | null> {
  try {
    const projectToken = await checkProjectNpmrc(cwd)
    if (projectToken) {
      return projectToken
    }

    if (packageManager === "npm") {
      const npmAuthToken = await getNpmAuthToken(cwd)
      return npmAuthToken
    }

    const globalAuthToken = await checkGlobalNpmrc()
    if (globalAuthToken) {
      return globalAuthToken
    }

    // Older CLI versions wrote yarn 1 tokens into `~/.yarnrc` via
    // `yarn config set --location=project` (which Yarn classic silently
    // misroutes to the global file). Read it as a fallback so `status` and
    // auth checks don't lie when the legacy save path was used.
    if (packageManager === "yarn") {
      return await checkGlobalYarnrc()
    }

    return null
  } catch (error) {
    return null
  }
}

/**
 * Check project .npmrc for auth token
 */
export async function checkProjectNpmrc(cwd: string): Promise<string | null> {
  const projectNpmrcPath = path.join(cwd, ".npmrc")
  if (fs.existsSync(projectNpmrcPath)) {
    const content = await fs.readFile(projectNpmrcPath, "utf8")
    return extractAuthToken(content)
  }
  return null
}

/**
 * Get npm auth token using npm config command
 */
async function getNpmAuthToken(cwd: string): Promise<string | null> {
  const { stdout } = await execa("npm", ["config", "get", AUTH_TOKEN_KEY], {
    cwd,
  })

  return stdout && stdout !== "undefined" ? stdout.trim() : null
}

/**
 * Check global .npmrc file for auth token
 */
async function checkGlobalNpmrc(): Promise<string | null> {
  const globalNpmrcPath = path.join(os.homedir(), ".npmrc")

  if (fs.existsSync(globalNpmrcPath)) {
    const content = await fs.readFile(globalNpmrcPath, "utf8")
    return extractAuthToken(content)
  }

  return null
}

/**
 * Check legacy Yarn classic `~/.yarnrc` for auth token. The file uses
 * `"key" value` lines (no `=`), so it isn't matched by `extractAuthToken`.
 */
async function checkGlobalYarnrc(): Promise<string | null> {
  const yarnrcPath = path.join(os.homedir(), ".yarnrc")
  if (!fs.existsSync(yarnrcPath)) {
    return null
  }
  const content = await fs.readFile(yarnrcPath, "utf8")
  const match = content
    .split("\n")
    .map((line) =>
      line.match(/^"\/\/registry\.tiptap\.dev\/:_authToken"\s+"?([^"\s]+)"?/)
    )
    .find((m): m is RegExpMatchArray => m !== null)
  return match?.[1] ?? null
}

/**
 * Extract auth token from config string
 */
export function extractAuthToken(configString: string): string | null {
  // Split into lines and filter for registry.tiptap.dev
  const lines = configString
    .split("\n")
    .filter((line) => line.startsWith("//registry.tiptap.dev/:_authToken="))

  if (lines.length === 0) {
    return null
  }

  // Extract the token after the "=" sign
  const token = lines[0].split("=")[1]?.trim()
  return token || null
}
